/**
 * Pre-programmed route fly-through: GPX loading, parsing, and coordinate
 * conversion (docs/plans/2026-07-02-route-flythrough-design.md).
 *
 * Hard constraint #1 (analytical DEM raster is the only source of LOS
 * truth): this route — and the camera/tarmac ribbon built from it in
 * camera/routePlayer.ts and terrain/routeRibbon.ts — is a community-facing
 * orientation aid only. It never feeds, informs, or substitutes for any
 * line-of-sight result.
 *
 * Coordinate transform: uses the shared visual-only EPSG:27700 <-> WGS84
 * conversion in visualProjection.ts (issue #40), a 7-parameter Helmert
 * transform, not the OSTN15 grid shift the pipeline uses elsewhere. Planar
 * error is roughly 1-3 m. That is acceptable here because this module's
 * output is visual only — see the design doc's "Accuracy note" and
 * visualProjection.ts's ACCURACY_NOTE. Do not reuse it for anything that
 * needs to agree with raypath/'s analytical coordinates.
 */
import { bngToLocal, type LocalXZ } from './coordinates';
import { wgs84ToBng } from './visualProjection';

export interface LatLon {
  lat: number;
  lon: number;
}

export interface RoutePolyline {
  points: LocalXZ[];
  /** Distance in metres from the route start to each point, same length/order as `points`. */
  cumulativeDistances: number[];
  /** Total route length in metres (== cumulativeDistances[cumulativeDistances.length - 1]). */
  totalLength: number;
}

/**
 * Extracts `<trkpt lat="" lon="">` points, in document order, from GPX XML
 * text. Deliberately ignores `<wpt>` (waypoints) — only the track itself
 * defines the fly-through path.
 */
export function parseGpx(xmlText: string): LatLon[] {
  const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    throw new Error(`failed to parse GPX XML: ${parserError.textContent}`);
  }
  const trkpts = Array.from(doc.getElementsByTagName('trkpt'));
  return trkpts.map((el) => {
    const lat = Number(el.getAttribute('lat'));
    const lon = Number(el.getAttribute('lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      throw new Error(`trkpt with non-numeric lat/lon: lat="${el.getAttribute('lat')}" lon="${el.getAttribute('lon')}"`);
    }
    return { lat, lon };
  });
}

/**
 * Converts WGS84 lat/lon points to local viewer-space (x, z) via
 * wgs84ToBng -> bngToLocal, and computes cumulative along-route distance.
 * Throws on an empty input rather than fabricating an empty-but-plausible
 * polyline (hard constraint #7).
 */
export function routeToLocal(latlon: LatLon[]): RoutePolyline {
  if (latlon.length === 0) {
    throw new Error('routeToLocal: cannot build a polyline from zero points');
  }
  const points: LocalXZ[] = latlon.map(({ lat, lon }) => {
    const { easting, northing } = wgs84ToBng(lon, lat);
    return bngToLocal(easting, northing);
  });

  const cumulativeDistances: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const step = Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
    cumulativeDistances.push(cumulativeDistances[i - 1] + step);
  }

  return {
    points,
    cumulativeDistances,
    totalLength: cumulativeDistances[cumulativeDistances.length - 1],
  };
}

/** Fetches a GPX file and returns its parsed, converted route polyline. */
export async function loadRoute(url: string): Promise<RoutePolyline> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`failed to fetch route GPX at ${url}: ${res.status} ${res.statusText}`);
  }
  const xmlText = await res.text();
  const latlon = parseGpx(xmlText);
  return routeToLocal(latlon);
}
